/**
 * PATH: packages/ui/src/kanban/components/settings-home/SettingsStatusRibbon.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-STATUS-001
 * PURPOSE: Visible loading, error, sync, and conflict state for settings prefs.
 * ROLE IN ARCHITECTURE: Presentational ribbon over the board preference runtime.
 * MAIN EXPORTS: SettingsStatusRibbon, summarizeSettingsSync.
 * NON-RESPONSIBILITIES: Does not retry transport or mutate drafts.
 * NOTES FOR FUTURE AI: Multi-tab sync already lives in the store; this only shows it.
 */

import { useEffect, useRef } from 'react'

import type { ViewPreferenceSyncState } from '@quotech/api/view-preferences'

import { trackKanbanSettingsEvent } from '../../../diagnostics/settingsTelemetry'
import { useOptionalKanbanSettingsPreferences } from '../../preferences/KanbanSettingsPreferenceContext'

export function summarizeSettingsSync(
  states: ViewPreferenceSyncState[],
): { label: string; tone: 'idle' | 'pending' | 'error' | 'conflict' } {
  if (states.includes('conflict')) return { label: 'Conflict — keep this draft or accept the other tab', tone: 'conflict' }
  if (states.includes('error')) return { label: 'Sync failed — local draft is still here', tone: 'error' }
  if (states.includes('hydrating')) return { label: 'Loading saved settings…', tone: 'pending' }
  if (states.includes('pending')) return { label: 'Saving…', tone: 'pending' }
  if (states.includes('synced')) return { label: 'Synced across tabs', tone: 'idle' }
  return { label: '', tone: 'idle' }
}

export function SettingsStatusRibbon() {
  const runtime = useOptionalKanbanSettingsPreferences()
  const lastTone = useRef<string | null>(null)
  const hydrating = Boolean(runtime && [
    runtime.cardFace.syncState,
    runtime.cardTabLayout.syncState,
    runtime.drawerSections.syncState,
  ].includes('hydrating'))
  const summary = summarizeSettingsSync(
    runtime
      ? [runtime.cardFace.syncState, runtime.cardTabLayout.syncState, runtime.drawerSections.syncState]
      : [],
  )
  useEffect(() => {
    if (!runtime || !summary.label || lastTone.current === summary.tone) return
    lastTone.current = summary.tone
    if (hydrating) {
      trackKanbanSettingsEvent('kanban.pref_hydrate', {
        scope: 'global',
        status: 'pending',
        controlId: 'pref.hydrate',
      })
      return
    }
    trackKanbanSettingsEvent('kanban.pref_sync', {
      scope: 'global',
      status: summary.tone === 'idle' ? 'success' : summary.tone,
      controlId: 'pref.sync',
    })
  }, [runtime, hydrating, summary.label, summary.tone])
  if (!runtime) return null
  const error = runtime.cardFace.error || runtime.cardTabLayout.error || runtime.drawerSections.error
  if (!summary.label && !error) return null
  const toneClass =
    summary.tone === 'conflict'
      ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
      : summary.tone === 'error'
        ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-200'
        : summary.tone === 'pending'
          ? 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200'
          : 'bg-neutral-50 text-neutral-600 dark:bg-zinc-900 dark:text-zinc-300'
  return (
    <div
      data-testid="settings-sync-status"
      data-sync-tone={summary.tone}
      role="status"
      className={`border-b px-4 py-1.5 text-[11px] ${toneClass}`}
    >
      {error && summary.tone === 'error' ? error : summary.label}
    </div>
  )
}
